/**
 * Payload-driven Tietopohja IA adapter.
 *
 * Reads live `GET /kb/equipment/{type}` fields only — no per-type topology catalogs.
 * AHU keeps its own adapter for air-path interface ports and power-facet rules.
 */

import type {
  KBCriterionText,
  KBEquipmentType,
  KBOperationalCriterion,
} from "@/lib/api/agent";
import {
  dossierSignalsForComponent,
  kbEntityNameFi,
  type KbDependencyGroup,
  type KbPathChain,
  type KbProposedChainRule,
  type KbUnitLevelSignal,
  type KbUnitSignalGroup,
} from "@/lib/kb-ia";
import { criterionSourceLabels } from "@/lib/kb-provenance";

export const DISTRICT_HEATING_SUBSTATION_TYPE = "district-heating-substation";
export const CHILLER_TYPE = "chiller";

function ruleFromCriterion(
  criterion: KBOperationalCriterion,
  criteriaText: Record<string, KBCriterionText>,
  component_ids: string[],
  kb: KBEquipmentType,
): KbProposedChainRule {
  const text = criteriaText[criterion.id];
  return {
    id: criterion.id,
    name_fi: criterion.name_fi,
    compares: [],
    observation_fi: text?.observation_fi,
    explanation_fi: text?.explanation_fi ?? undefined,
    remedy_fi: text?.remedy_fi ?? undefined,
    layer: "monitoring",
    status: "in-kb",
    component_ids,
    component_labels_fi: component_ids.map((id) => kbEntityNameFi(kb, id)),
    source_labels: criterionSourceLabels(criterion.claims),
  };
}

/** Vaikutusketjut: live `analyses[]` union path criteria on chain components. */
export function buildPathChainsFromKb(
  kb: KBEquipmentType,
  criteriaText: Record<string, KBCriterionText> = {},
): KbPathChain[] {
  const componentById = new Map(kb.components.map((c) => [c.id, c]));

  return (kb.analyses ?? []).map((analysis) => {
    const byId = new Map<string, KbProposedChainRule>();

    for (const criterion of analysis.operational_criteria ?? []) {
      byId.set(
        criterion.id,
        ruleFromCriterion(criterion, criteriaText, [], kb),
      );
    }

    for (const step of analysis.chain) {
      if (step.component === "equipment") continue;
      const component = componentById.get(step.component);
      if (!component) continue;
      for (const criterion of component.operational_criteria ?? []) {
        if (byId.has(criterion.id)) continue;
        byId.set(
          criterion.id,
          ruleFromCriterion(criterion, criteriaText, [component.id], kb),
        );
      }
    }

    return {
      id: analysis.id,
      name_fi: analysis.name_fi,
      description_fi: analysis.description_fi,
      source: "kb" as const,
      steps: analysis.chain.map((step) => ({
        label_fi: step.label_fi,
        component: step.component,
        data_point: step.data_point,
      })),
      proposed_rules: [...byId.values()],
    };
  });
}

/** Riippuvuudet: live `dependency_groups[]` + matching `connection_analyses`. */
export function buildDependencyGroupsFromKb(
  kb: KBEquipmentType,
  criteriaText: Record<string, KBCriterionText> = {},
): KbDependencyGroup[] {
  const analysesById = new Map(
    (kb.connection_analyses ?? []).map((a) => [a.id, a]),
  );
  const groups = kb.dependency_groups ?? [];

  return groups.map((group) => {
    const rules: KbProposedChainRule[] = [];
    for (const analysisId of group.analysis_ids) {
      const analysis = analysesById.get(analysisId);
      if (!analysis) continue;
      const criteria = analysis.operational_criteria ?? [];
      if (criteria.length > 0) {
        for (const criterion of criteria) {
          rules.push(ruleFromCriterion(criterion, criteriaText, [], kb));
        }
      } else {
        rules.push({
          id: analysis.id,
          name_fi: analysis.name_fi,
          compares: [],
          observation_fi: analysis.description_fi || undefined,
          layer: "monitoring",
          status: "connection",
        });
      }
    }

    return {
      id: group.id,
      counterpart_type: group.counterpart_type,
      name_fi: group.name_fi,
      description_fi: group.description_fi,
      signals: group.boundary_signals.map((s) => ({
        id: s.id,
        name_fi: s.name_fi,
        side: s.side === "self" ? ("self" as const) : ("peer" as const),
        unit: s.unit || undefined,
      })),
      rules,
    };
  });
}

function unitGroupForSignalId(id: string): KbUnitSignalGroup {
  if (id.includes("outdoor") || id.includes("fresh")) return "outdoor";
  if (id.includes("setpoint")) return "setpoint";
  if (id.includes("flow") || id.includes("airflow")) return "flow";
  if (id.includes("pressure") || id.includes("dp")) return "pressure";
  if (id.includes("extract") || id.includes("exhaust")) return "extract";
  return "supply";
}

/** Laitteen taso Signaalit from `shared_data_points`. */
export function buildUnitLevelSignalsFromKb(
  kb: KBEquipmentType,
): KbUnitLevelSignal[] {
  return (kb.shared_data_points ?? []).map((dp) => ({
    id: dp.id,
    name_fi: dp.name_fi,
    unit: dp.unit,
    criticality: dp.criticality,
    group: unitGroupForSignalId(dp.id),
    owner: "shared" as const,
  }));
}

/** Component → Virta ids derived from live `analyses[].chain`. */
export function componentAirPathsFromKb(
  kb: KBEquipmentType,
): Record<string, string[]> {
  const map = new Map<string, Set<string>>();
  for (const analysis of kb.analyses ?? []) {
    for (const step of analysis.chain) {
      if (step.component === "equipment") continue;
      const set = map.get(step.component) ?? new Set<string>();
      set.add(analysis.id);
      map.set(step.component, set);
    }
  }
  return Object.fromEntries(
    [...map.entries()].map(([id, set]) => [id, [...set]]),
  );
}

/** Behavior ids whose live home nests under this component. */
export function behaviorIdsUnderComponentFromKb(
  kb: KBEquipmentType,
  componentId: string,
): string[] {
  return kb.behaviors
    .filter(
      (b) =>
        b.home === "component" && (b.under_ids ?? []).includes(componentId),
    )
    .map((b) => b.id);
}

export function dossierSignalsForOwnedComponent(
  componentId: string,
  owned: { id: string; name_fi: string; unit?: string; criticality?: string }[],
) {
  return dossierSignalsForComponent(componentId, owned);
}
